import React, { useCallback, useEffect, useState } from 'react';
import useOrganisation from '../hooks/useOrganisation';
import organisationService from '../services/organisationService';
import FormField from '../components/common/FormField';
import Alert from '../components/common/Alert';
import Modal from '../components/common/Modal';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { ORGANISATION_TYPES } from '../utils/propertyOptions';
import { formatDate } from '../utils/formatters';
import './ApplicantPages.css';
import './OrganisationPage.css';

const ROLE_LABELS = {
  owner: 'Owner',
  admin: 'Administrator',
  agent: 'Agent',
  verifier: 'Verifier',
  readonly: 'Read-only'
};

const ROLE_HELP = {
  admin: 'Manages the team and sees the dashboard',
  agent: 'Adds properties and works with applicants',
  verifier: 'Reviews verification tasks',
  readonly: 'Can look but not change anything'
};

const messageFrom = (err, fallback) => {
  const errors = err.response?.data?.errors;
  if (errors) return Object.values(errors).flat().join(' ');
  return err.response?.data?.message || err.message || fallback;
};

// Mirrors the server's rules (the server enforces them): the owner is never changed, admins do not manage other
// admins, and nobody changes their own role.
const canChange = (myRole, member) =>
  ['owner', 'admin'].includes(myRole) && member.role !== 'owner' && !member.is_you && !(myRole === 'admin' && member.role === 'admin');

const OrganisationPage = () => {
  const { organisations, organisation, select, loading, error } = useOrganisation();
  const [details, setDetails] = useState(null);
  const [members, setMembers] = useState(null);
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(false);
  const [invite, setInvite] = useState({ email: '', role: 'agent' });
  const [removing, setRemoving] = useState(null);

  const id = organisation?.id;

  const load = useCallback(async () => {
    if (!id) return;
    const [org, team] = await Promise.all([organisationService.get(id), organisationService.members(id)]);
    setDetails({ name: org.name, type: org.type, registration_number: org.registration_number || '', verification_status: org.verification_status });
    setMembers(team);
  }, [id]);

  useEffect(() => {
    setMembers(null);
    load().catch((err) => setNotice({ type: 'error', message: messageFrom(err, 'This organisation could not be loaded.') }));
  }, [load]);

  if (loading) return <LoadingSpinner />;
  if (!organisation) return <Alert type="error" message={error || 'You are not part of an organisation.'} />;
  if (!details || !members) return <LoadingSpinner />;

  const me = members.find((m) => m.is_you);
  const myRole = me?.role;
  const canManage = ['owner', 'admin'].includes(myRole);
  const roleOptions = (myRole === 'owner' ? ['admin', 'agent', 'verifier', 'readonly'] : ['agent', 'verifier', 'readonly']).map((r) => ({ value: r, label: ROLE_LABELS[r] }));

  const run = async (action, success) => {
    setBusy(true);
    setNotice(null);
    try {
      await action();
      await load();
      if (success) setNotice({ type: 'success', message: success });
    } catch (err) {
      setNotice({ type: 'error', message: messageFrom(err, 'That did not work.') });
    } finally {
      setBusy(false);
    }
  };

  const saveDetails = (e) => {
    e.preventDefault();
    run(() => organisationService.update(id, { name: details.name.trim(), type: details.type, registration_number: details.registration_number || null }), 'Details saved.');
  };

  const sendInvite = (e) => {
    e.preventDefault();
    run(async () => {
      const result = await organisationService.invite(id, invite.email.trim(), invite.role);
      setInvite({ email: '', role: 'agent' });
      setNotice({ type: 'success', message: result.message });
    });
  };

  const confirmRemove = () => {
    const member = removing;
    setRemoving(null);
    run(() => organisationService.removeMember(id, member.id), member.is_you ? 'You left the organisation.' : `${member.first_name} ${member.last_name} was removed.`);
  };

  return (
    <div className="ap-page">
      <h1>Organisation</h1>

      {organisations.length > 1 && (
        <FormField
          label="Organisation"
          name="organisation"
          type="select"
          options={organisations.map((o) => ({ value: String(o.id), label: o.name }))}
          value={String(id)}
          onChange={(e) => select(e.target.value)}
        />
      )}

      {notice && <Alert type={notice.type} message={notice.message} dismissible onClose={() => setNotice(null)} />}

      <section className="ap-card" aria-labelledby="org-details-title">
        <h2 id="org-details-title">Details</h2>
        <p className="ap-muted">
          {details.verification_status === 'verified' ? 'This organisation is verified.' : 'This organisation has not been verified yet.'}
        </p>
        <form onSubmit={saveDetails} noValidate>
          <FormField label="Name" name="name" value={details.name} onChange={(e) => setDetails({ ...details, name: e.target.value })} required disabled={!canManage} />
          <FormField label="Type" name="type" type="select" options={ORGANISATION_TYPES} value={details.type} onChange={(e) => setDetails({ ...details, type: e.target.value })} disabled={!canManage} />
          <FormField label="Company registration number (optional)" name="registration_number" value={details.registration_number} onChange={(e) => setDetails({ ...details, registration_number: e.target.value })} disabled={!canManage} />
          {canManage && <button type="submit" className="btn-primary ap-button" disabled={busy || !details.name.trim()}>Save details</button>}
        </form>
      </section>

      <section className="ap-card" aria-labelledby="team-title">
        <h2 id="team-title">Team</h2>
        <ul className="ap-rows">
          {members.map((m) => (
            <li key={m.id}>
              <div>
                <strong>{m.first_name} {m.last_name}</strong>{m.is_you && <span className="ap-muted"> (you)</span>}
                <div className="ap-muted">{m.email}</div>
                {m.status === 'pending' ? (
                  <span className="ap-pill">Invited, waiting to accept</span>
                ) : (
                  m.joined_at && <div className="ap-muted">Joined {formatDate(m.joined_at)}</div>
                )}
              </div>
              <div className="org-actions">
                {canChange(myRole, m) && m.status === 'active' ? (
                  <select
                    aria-label={`Role for ${m.first_name} ${m.last_name}`}
                    value={m.role}
                    disabled={busy}
                    onChange={(e) => run(() => organisationService.changeRole(id, m.id, e.target.value), 'Role updated.')}
                  >
                    {!roleOptions.some((o) => o.value === m.role) && <option value={m.role}>{ROLE_LABELS[m.role]}</option>}
                    {roleOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                ) : (
                  <span className="ap-pill">{ROLE_LABELS[m.role] || m.role}</span>
                )}
                {canChange(myRole, m) && (
                  <button type="button" className="ap-button ap-button--danger" onClick={() => setRemoving(m)} disabled={busy}>
                    {m.status === 'pending' ? 'Cancel invitation' : 'Remove'}
                  </button>
                )}
                {m.is_you && m.role !== 'owner' && (
                  <button type="button" className="ap-button ap-button--ghost" onClick={() => setRemoving(m)} disabled={busy}>Leave</button>
                )}
              </div>
            </li>
          ))}
        </ul>
      </section>

      {canManage && (
        <section className="ap-card" aria-labelledby="invite-team-title">
          <h2 id="invite-team-title">Invite someone</h2>
          <p className="ap-muted">
            They get an invitation to accept. They are only added once they do. If they do not have an account yet, we email
            them how to create one.
          </p>
          <form onSubmit={sendInvite} noValidate>
            <FormField label="Email address" name="invite_email" type="email" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} required />
            <FormField
              label="Role"
              name="invite_role"
              type="select"
              options={roleOptions.map((o) => ({ value: o.value, label: `${o.label}: ${ROLE_HELP[o.value]}` }))}
              value={invite.role}
              onChange={(e) => setInvite({ ...invite, role: e.target.value })}
            />
            <button type="submit" className="btn-primary ap-button" disabled={busy || !invite.email}>Send invitation</button>
          </form>
        </section>
      )}

      <Modal isOpen={!!removing} onClose={() => setRemoving(null)} title={removing?.is_you ? 'Leave this organisation?' : removing?.status === 'pending' ? 'Cancel this invitation?' : 'Remove this person?'}>
        <p>
          {removing?.is_you
            ? 'You will lose access to this organisation\'s properties and applicants straight away.'
            : removing?.status === 'pending'
              ? `${removing?.first_name} ${removing?.last_name} will no longer be able to accept.`
              : `${removing?.first_name} ${removing?.last_name} loses access to this organisation's properties and applicants straight away.`}
        </p>
        <div className="prop-actions">
          <button type="button" className="ap-button ap-button--danger" onClick={confirmRemove}>
            {removing?.is_you ? 'Leave' : removing?.status === 'pending' ? 'Cancel invitation' : 'Remove'}
          </button>
          <button type="button" className="ap-button ap-button--ghost" onClick={() => setRemoving(null)}>Keep</button>
        </div>
      </Modal>
    </div>
  );
};

export default OrganisationPage;
